import type { ReactNode } from 'react';

import { Notice } from '../Notice/Notice.tsx';
import { SearchPicker, type SearchPickerStatus } from '../SearchPicker/SearchPicker.tsx';
import styles from './CodedValuePicker.module.css';

/** One code a person can pick: a diagnosis, lab test or service code. */
export interface CodedValueOption {
  /** Stable key for the row, e.g. the concept ID or system and code. */
  key: string;
  code: string;
  display: string;
  /** The synonym that matched, when it isn't the display. */
  matchedTerm: string | null;
  /** The parent's title (for ICD-11, the block or chapter), if known. */
  parentDisplay: string | null;
}

/** Where the national terminology service fallback is (plan §3.3). */
export type NationalSearchState =
  'not_needed' | 'results' | 'unavailable' | 'not_configured' | 'not_requested';

export type CodedValueSource = 'local' | 'national_service';

export interface CodedValuePickerProps {
  /** Always visible, above the field, e.g. "Diagnosis (ICD-11)". */
  label: string;
  description?: string;
  inputValue: string;
  onInputChange: (value: string) => void;
  status: SearchPickerStatus;
  /** The text these results answer; results for other text are hidden as stale. */
  itemsQuery?: string;
  /** Results from Wodi's own copy of the code system, best match first. */
  local: readonly CodedValueOption[];
  /** Results from the national service, asked only when nothing matched locally. */
  national: { state: NationalSearchState; items: readonly CodedValueOption[] };
  onSelect: (option: CodedValueOption, source: CodedValueSource) => void;
  errorMessage?: string;
  onRetry?: () => void;
  autoFocus?: boolean;
}

function Row({ option }: { option: CodedValueOption }): ReactNode {
  const details = [
    option.matchedTerm === null ? null : `matched: ${option.matchedTerm}`,
    option.parentDisplay,
  ].filter((d): d is string => d !== null);
  return (
    <span className={styles.row}>
      <span className={styles.main}>
        <span className={`wodi-id ${styles.code}`}>{option.code}</span>
        <span>{option.display}</span>
      </span>
      {details.length === 0 ? null : <span className={styles.details}>{details.join(' · ')}</span>}
    </span>
  );
}

/**
 * Type-to-search picker for coded values (DESIGN.md §7; plan §6.1): the code in the
 * identifier font, the title, and in pencil the synonym that matched and the parent.
 *
 * Local results come first and alone. Only when nothing matches locally does the picker
 * show results from the national terminology service, labelled as such, so the two groups
 * are never mixed. Order is exactly what the server returned.
 */
export function CodedValuePicker({
  label,
  description,
  inputValue,
  onInputChange,
  status,
  itemsQuery,
  local,
  national,
  onSelect,
  errorMessage,
  onRetry,
  autoFocus,
}: CodedValuePickerProps) {
  const showNational = local.length === 0 && national.items.length > 0;
  const items = showNational ? national.items : local;
  const source: CodedValueSource = showNational ? 'national_service' : 'local';

  const emptyMessage =
    national.state === 'unavailable'
      ? "No codes match. The national terminology service didn't respond, so only local codes were searched. Check the spelling or search by code."
      : 'No codes match. Check the spelling or search by code.';

  return (
    <div className={styles.picker}>
      <SearchPicker<CodedValueOption>
        label={label}
        {...(description === undefined ? {} : { description })}
        inputValue={inputValue}
        onInputChange={onInputChange}
        status={status}
        items={items}
        {...(itemsQuery === undefined ? {} : { itemsQuery })}
        getKey={(o) => `${source}:${o.key}`}
        getTextValue={(o) => `${o.code} ${o.display}`}
        renderItem={(o) => <Row option={o} />}
        onSelect={(o) => {
          onSelect(o, source);
        }}
        isExactMatch={(o, q) => o.code.toLowerCase() === q.trim().toLowerCase()}
        promptMessage="Type at least two letters of the name, or the code."
        emptyMessage={emptyMessage}
        {...(errorMessage === undefined ? {} : { errorMessage })}
        {...(onRetry === undefined ? {} : { onRetry })}
        offlineMessage="Searching codes needs a connection to Wodi."
        countMessage={(n) => `${String(n)} ${n === 1 ? 'code' : 'codes'} found`}
        {...(autoFocus === undefined ? {} : { autoFocus })}
      />
      {showNational && status === 'ready' ? (
        <Notice tone="info">
          No local codes match. These results come from the national terminology service; your
          search was sent there.
        </Notice>
      ) : null}
    </div>
  );
}
